import {
  AIR_SPEED,
  BLOCK_HITS_TO_BREAK,
  CAST_COOLDOWN,
  CAST_SPAWN_TIME,
  COYOTE_TIME,
  CREEP_SPEED,
  CRUMBLE_TIME,
  DRIFTER_STEP_INTERVAL,
  DT,
  EPS,
  GRAVITY,
  GRID_H,
  GRID_W,
  JUMP_BUFFER,
  JUMP_VELOCITY,
  MAX_FALL_SPEED,
  MOONSHARD_VALUE,
  PLAYER_H,
  PLAYER_HALF_W,
  RANGED_COOLDOWN,
  RANGED_RANGE,
  SOWER_INTERVAL,
  WALK_ACCEL,
  WALK_DECEL,
  WALK_SPEED,
} from './constants.ts';
import { createState, idx, inBounds, isSolid, tileAt } from './room.ts';
import {
  Btn,
  ChimeraKind,
  FailReason,
  ItemKind,
  Outcome,
  Phase,
  STARTER_LOADOUT,
  Tile,
  type Chimera,
  type InputFrame,
  type Loadout,
  type RoomDef,
  type SimEvent,
  type SimState,
} from './types.ts';

const CREEP_HALF_W = 0.35;
const CREEP_H = 0.8;

/**
 * The deterministic core of the game. Pure TypeScript: no Three.js, no React, no
 * DOM, no Date, no Math.random. Given the same room and the same input sequence
 * it produces a bit-identical result every time, on every run.
 *
 * That property is not a nicety — it is what makes the CI solvability harness
 * (tests/solvability.test.ts) possible, and it is why this module is built and
 * tested before a renderer exists at all.
 */
export class Simulation {
  readonly room: RoomDef;
  state: SimState;
  private readonly loadout: Loadout;
  private readonly out: SimEvent[] = [];
  /** UI-owned accessibility setting. The default preserves the exact
   * Starter-loadout simulation used by every recorded solution. */
  private moonlightDrainMultiplier = 1;

  constructor(room: RoomDef, loadout: Loadout = STARTER_LOADOUT) {
    this.room = room;
    this.loadout = loadout;
    this.state = createState(room);
  }

  reset(): void {
    this.state = createState(this.room);
    this.out.length = 0;
  }

  /** Assist Mode slows only the room timer; physics, AI and tick order stay
   * deterministic. Values are clamped so a malformed UI value cannot freeze
   * or reverse time. */
  setMoonlightDrainMultiplier(multiplier: number): void {
    this.moonlightDrainMultiplier = Math.max(0.5, Math.min(1, multiplier));
  }

  /** Advance exactly one tick. Tick order is fixed: input, player, blocks,
   *  chimera, collisions, items (brief §5). */
  step(buttons: InputFrame): readonly SimEvent[] {
    this.out.length = 0;
    const s = this.state;
    if (s.outcome !== Outcome.Playing) return this.out;

    s.tick += 1;

    s.moonlight -= DT * this.moonlightDrainMultiplier;
    if (s.moonlight <= 0) {
      s.moonlight = 0;
      this.fail(FailReason.Moonlight);
      return this.out;
    }

    this.updatePlayer(buttons);
    this.updateBlocks();
    this.updateChimera();
    this.checkContact();
    this.checkItemsAndPortal(buttons);
    this.checkDoors(buttons);

    s.player.prevButtons = buttons;
    return this.out;
  }

  // ---------------------------------------------------------------- player

  private updatePlayer(buttons: InputFrame): void {
    const s = this.state;
    const p = s.player;
    const pressed = buttons & ~p.prevButtons;

    const dir = ((buttons & Btn.Right) !== 0 ? 1 : 0) - ((buttons & Btn.Left) !== 0 ? 1 : 0);
    // Airborne movement is capped below walk speed so a jump's reach stays
    // predictable at ~1.5 tiles, whatever the run-up was.
    const maxSpeed = p.grounded ? WALK_SPEED : AIR_SPEED;
    if (dir !== 0) {
      p.facing = dir > 0 ? 1 : -1;
      p.vx += dir * WALK_ACCEL * DT;
      if (p.vx > maxSpeed) p.vx = maxSpeed;
      if (p.vx < -maxSpeed) p.vx = -maxSpeed;
    } else if (p.vx !== 0) {
      const drop = WALK_DECEL * DT;
      p.vx = p.vx > 0 ? Math.max(0, p.vx - drop) : Math.min(0, p.vx + drop);
    }

    if ((pressed & Btn.Jump) !== 0) p.jumpBuffer = JUMP_BUFFER;
    p.jumpBuffer = Math.max(0, p.jumpBuffer - DT);
    p.coyote = p.grounded ? COYOTE_TIME : Math.max(0, p.coyote - DT);

    if (p.jumpBuffer > 0 && p.coyote > 0) {
      // Shed any run-up above the air cap at takeoff, so every jump covers the
      // same ground whether it started from a standstill or a full run.
      if (p.vx > AIR_SPEED) p.vx = AIR_SPEED;
      if (p.vx < -AIR_SPEED) p.vx = -AIR_SPEED;
      p.vy = -JUMP_VELOCITY;
      p.jumpBuffer = 0;
      p.coyote = 0;
      p.grounded = false;
      this.out.push({ type: 'player:jump' });
    }

    p.vy += GRAVITY * DT;
    if (p.vy > MAX_FALL_SPEED) p.vy = MAX_FALL_SPEED;

    this.movePlayerX();
    this.movePlayerY();

    p.castCooldown = Math.max(0, p.castCooldown - DT);
    if ((pressed & Btn.Cast) !== 0 && p.castCooldown <= 0) this.doCast(buttons);

    p.rangedCooldown = Math.max(0, p.rangedCooldown - DT);
    if ((pressed & Btn.Ranged) !== 0 && p.rangedCooldown <= 0) this.fireRanged();

    if (p.y > GRID_H + 2) this.fail(FailReason.Void);
  }

  /**
   * The Tail skill (brief §5, §8). Fires along the player's feet row in the
   * facing direction, stopping at the first solid tile - it cannot shoot
   * through a wall - or the first living Chimera, which it kills outright.
   *
   * This is the only verb allowed to touch a Warden without a block. Normally
   * gated by rangedCharges, which only the TailCharm item grants - except the
   * Bramble Horn (brief §7, changed 2026-09-05 from the block-stacking cast
   * variant this unlock originally shipped as) bypasses the charge gate
   * entirely, so a Round 1 player can fire this skill for the first time
   * without ever needing an item Round 1 never authors into a room. Charges
   * still drain first when any exist, so a real TailCharm pickup (Round 2)
   * remains meaningful ammo rather than being made irrelevant by the Horn.
   * An empty gun with the Horn locked still spends the press's feedback (a
   * distinct 'ranged:empty' event) so pressing it is never silently nothing.
   */
  private fireRanged(): void {
    const s = this.state;
    const p = s.player;
    if (p.rangedCharges <= 0 && !this.loadout.brambleHorn) {
      this.out.push({ type: 'ranged:empty' });
      return;
    }
    if (p.rangedCharges > 0) p.rangedCharges -= 1;
    p.rangedCooldown = RANGED_COOLDOWN;

    // Travelled distance is tracked so 'ranged:fired' can carry the shot's
    // real length - a renderer needs that to draw a beam that actually
    // stops at the wall or the kill, rather than always drawing full range.
    const feetRow = Math.floor(p.y - EPS);
    const originTx = Math.floor(p.x) + p.facing;
    let tiles = 0;
    for (let step = 1; step <= RANGED_RANGE; step++) {
      const tx = Math.floor(p.x) + p.facing * step;
      if (!inBounds(tx, feetRow) || isSolid(s, tx, feetRow)) break;
      tiles = step;
      const target = s.chimera.find((c) => {
        if (!c.alive) return false;
        const t = this.chimeraTile(c);
        return t.tx === tx && t.ty === feetRow;
      });
      if (target) {
        target.alive = false;
        this.out.push({ type: 'chimera:banished', id: target.id, cause: 'ranged' });
        break;
      }
    }
    // Even a shot blocked on the very first tile still flashes at the
    // muzzle rather than drawing nothing - the press must always be visible.
    this.out.push({
      type: 'ranged:fired',
      tx: originTx,
      ty: feetRow,
      tiles: Math.max(tiles, 1),
      facing: p.facing,
    });
  }

  private movePlayerX(): void {
    const s = this.state;
    const p = s.player;
    const dx = p.vx * DT;
    if (dx === 0) return;
    p.x += dx;

    const y0 = Math.floor(p.y - PLAYER_H + EPS);
    const y1 = Math.floor(p.y - EPS);

    if (dx > 0) {
      const tx = Math.floor(p.x + PLAYER_HALF_W - EPS);
      for (let ty = y0; ty <= y1; ty++) {
        if (isSolid(s, tx, ty)) {
          p.x = tx - PLAYER_HALF_W - EPS;
          p.vx = 0;
          return;
        }
      }
    } else {
      const tx = Math.floor(p.x - PLAYER_HALF_W + EPS);
      for (let ty = y0; ty <= y1; ty++) {
        if (isSolid(s, tx, ty)) {
          p.x = tx + 1 + PLAYER_HALF_W + EPS;
          p.vx = 0;
          return;
        }
      }
    }
  }

  private movePlayerY(): void {
    const s = this.state;
    const p = s.player;
    const dy = p.vy * DT;
    const wasGrounded = p.grounded;
    p.grounded = false;
    if (dy === 0) return;
    p.y += dy;

    const x0 = Math.floor(p.x - PLAYER_HALF_W + EPS);
    const x1 = Math.floor(p.x + PLAYER_HALF_W - EPS);

    if (dy > 0) {
      const ty = Math.floor(p.y - EPS);
      for (let tx = x0; tx <= x1; tx++) {
        if (isSolid(s, tx, ty)) {
          p.y = ty;
          p.vy = 0;
          p.grounded = true;
          if (!wasGrounded) this.out.push({ type: 'player:land' });
          return;
        }
      }
    } else {
      const ty = Math.floor(p.y - PLAYER_H + EPS);
      let struck = false;
      for (let tx = x0; tx <= x1; tx++) {
        if (!isSolid(s, tx, ty)) continue;
        if (!struck) {
          p.y = ty + 1 + PLAYER_H;
          p.vy = 0;
          struck = true;
        }
        // Every solid tile the head actually meets takes a hit, so a player
        // straddling a boundary strikes both rather than an arbitrary one.
        this.hitBlockFromBelow(tx, ty);
      }
    }
  }

  /**
   * The core verb. The target is the tile adjacent in the facing direction, on
   * the row containing the player's feet — and casting is legal in mid-air,
   * which is the entire game (brief §5).
   */
  private doCast(buttons: InputFrame): void {
    const s = this.state;
    const p = s.player;
    const feetRow = Math.floor(p.y - EPS);

    // Holding down aims one row lower while still reaching ahead, so the target
    // is diagonally down-forward rather than straight underfoot. Aiming straight
    // down would let the player cut away their own footing, which is never what
    // they meant and is unrecoverable in mid-room.
    const down = (buttons & Btn.Down) !== 0;
    const ty = down ? feetRow + 1 : feetRow;
    const tx = Math.floor(p.x) + p.facing;

    if (!inBounds(tx, ty)) {
      this.out.push({ type: 'cast:refused', tx, ty });
      return;
    }

    const i = idx(tx, ty);
    const kind = tileAt(s, tx, ty);

    if (kind === Tile.Stone) {
      this.out.push({ type: 'cast:stone', tx, ty });
      p.castCooldown = CAST_COOLDOWN;
      return;
    }

    if (kind === Tile.Empty) {
      s.tiles[i] = Tile.Seed;
      s.phase[i] = Phase.Spawning;
      s.phaseTimer[i] = CAST_SPAWN_TIME;
      s.hits[i] = 0;
      // A cast block never kills a Chimera, including one standing on its own
      // target tile - only a skill (the ranged Tail) or an item (the Sunshard)
      // does that. The new block still blocks its path exactly like any other
      // obstacle, the same as casting one a tile ahead of a patrol would.
      // The target column is adjacent, so a player standing off-centre can have
      // their leading edge inside it. That used to refuse the cast outright,
      // which read as the cast randomly not working depending on where you
      // happened to stop. Push the player clear of the new block instead: the
      // cast always fires, and the game never silently ignores the input.
      this.hideAt(tx, ty);
      this.pushPlayerOutOf(tx, ty);
      this.out.push({ type: 'cast:create', tx, ty });
    } else {
      // A player straddling a tile boundary is supported by two columns, so the
      // diagonal target can still be one of them. Refuse rather than drop them.
      if (this.isSupportingPlayer(tx, ty)) {
        this.out.push({ type: 'cast:refused', tx, ty });
        p.castCooldown = CAST_COOLDOWN;
        return;
      }
      this.breakBlock(i, tx, ty);
    }

    p.castCooldown = CAST_COOLDOWN;
  }

  /** True when the tile is directly beneath the grounded player's feet box. */
  private isSupportingPlayer(tx: number, ty: number): boolean {
    const p = this.state.player;
    if (!p.grounded) return false;
    if (ty !== Math.floor(p.y - EPS) + 1) return false;
    const x0 = Math.floor(p.x - PLAYER_HALF_W + EPS);
    const x1 = Math.floor(p.x + PLAYER_HALF_W - EPS);
    return tx >= x0 && tx <= x1;
  }

  private breakBlock(i: number, tx: number, ty: number): void {
    const s = this.state;
    s.phase[i] = Phase.Crumbling;
    s.phaseTimer[i] = CRUMBLE_TIME;
    s.hits[i] = 0;
    this.out.push({ type: 'cast:destroy', tx, ty });
  }

  /**
   * Registers a head-butt on a castable block, breaking it on the second.
   *
   * Gives the player a way to clear a block without turning to face it, which
   * matters most under a staircase they have just built and now need to unmake.
   */
  private hitBlockFromBelow(tx: number, ty: number): void {
    const s = this.state;
    if (!inBounds(tx, ty)) return;
    const i = idx(tx, ty);
    const kind = tileAt(s, tx, ty);
    if (kind !== Tile.Seed && kind !== Tile.Vine) return;
    if ((s.phase[i] ?? Phase.Settled) !== Phase.Settled) return;

    const next = (s.hits[i] ?? 0) + 1;
    s.hits[i] = next;
    this.out.push({ type: 'block:hit', tx, ty, hits: next });
    if (next >= BLOCK_HITS_TO_BREAK) this.breakBlock(i, tx, ty);
  }

  /** Nudges the player just clear of a tile they overlap, along x. */
  private pushPlayerOutOf(tx: number, ty: number): void {
    const p = this.state.player;
    if (!this.tileOverlapsPlayer(tx, ty)) return;
    if (tx >= Math.floor(p.x)) {
      p.x = tx - PLAYER_HALF_W - EPS;
    } else {
      p.x = tx + 1 + PLAYER_HALF_W + EPS;
    }
    if ((p.vx > 0 && tx >= Math.floor(p.x)) || (p.vx < 0 && tx < Math.floor(p.x))) p.vx = 0;
  }

  private tileOverlapsPlayer(tx: number, ty: number): boolean {
    const p = this.state.player;
    return (
      p.x + PLAYER_HALF_W > tx + EPS &&
      p.x - PLAYER_HALF_W < tx + 1 - EPS &&
      p.y > ty + EPS &&
      p.y - PLAYER_H < ty + 1 - EPS
    );
  }

  // ---------------------------------------------------------------- blocks

  private updateBlocks(): void {
    const s = this.state;
    for (let i = 0; i < s.tiles.length; i++) {
      const ph = s.phase[i] ?? Phase.Settled;
      if (ph === Phase.Settled) continue;
      const t = (s.phaseTimer[i] ?? 0) - DT;
      if (t > 0) {
        s.phaseTimer[i] = t;
        continue;
      }
      s.phaseTimer[i] = 0;
      if (ph === Phase.Spawning) {
        s.phase[i] = Phase.Settled;
      } else {
        s.tiles[i] = Tile.Empty;
        s.phase[i] = Phase.Settled;
        s.hits[i] = 0;
        this.revealAt(i % GRID_W, Math.floor(i / GRID_W));
      }
    }
  }

  /**
   * Reveals anything the block that just vanished from this tile was
   * covering - items, doors, and the portal alike (brief pillar 5, direct
   * request to generalize what was originally an item-only mechanic).
   */
  private revealAt(tx: number, ty: number): void {
    const s = this.state;
    for (const it of s.items) {
      if (it.taken || !it.hidden) continue;
      if (it.tx !== tx || it.ty !== ty) continue;
      it.hidden = false;
      this.out.push({ type: 'item:revealed', id: it.id });
    }
    for (const door of s.doors) {
      if (!door.hidden) continue;
      if (door.tx !== tx || door.ty !== ty) continue;
      door.hidden = false;
      this.out.push({ type: 'door:revealed', id: door.id });
    }
    if (s.portal.hidden && s.portal.tx === tx && s.portal.ty === ty) {
      s.portal.hidden = false;
      this.out.push({ type: 'portal:revealed' });
    }
  }

  /**
   * Buries anything the new block covers - items, doors, and the portal
   * alike.
   *
   * This is the other half of reveal-on-destroy, and it makes the pair
   * reversible: bury a Moonseed (or a door, or the portal) under a block and
   * you have to cut it out again. It also means a player can hide something
   * from a Chimera, or from themselves by accident, which is exactly the
   * kind of self-inflicted problem a puzzle game should allow. None of this
   * changes reachability on its own - a Settled block already makes the tile
   * solid, which is what actually stops a door or the portal being used -
   * this only changes whether the game still shows them while that's true.
   */
  private hideAt(tx: number, ty: number): void {
    const s = this.state;
    for (const it of s.items) {
      if (it.taken || it.hidden) continue;
      if (it.tx !== tx || it.ty !== ty) continue;
      it.hidden = true;
      this.out.push({ type: 'item:hidden', id: it.id });
    }
    for (const door of s.doors) {
      if (door.hidden) continue;
      if (door.tx !== tx || door.ty !== ty) continue;
      door.hidden = true;
      this.out.push({ type: 'door:hidden', id: door.id });
    }
    if (!s.portal.hidden && s.portal.tx === tx && s.portal.ty === ty) {
      s.portal.hidden = true;
      this.out.push({ type: 'portal:hidden' });
    }
  }

  // --------------------------------------------------------------- chimera

  private chimeraTile(c: Chimera): { tx: number; ty: number } {
    if (c.kind === ChimeraKind.Creep) {
      return { tx: Math.floor(c.x), ty: Math.floor(c.y - EPS) };
    }
    return { tx: Math.floor(c.x), ty: Math.floor(c.y) };
  }

  private updateChimera(): void {
    const s = this.state;
    const spawned: Chimera[] = [];

    for (const c of s.chimera) {
      if (!c.alive) continue;
      switch (c.kind) {
        case ChimeraKind.Creep:
          this.updateCreep(c);
          break;
        case ChimeraKind.Drifter:
          this.updateDrifter(c, true);
          break;
        case ChimeraKind.Warden:
          // Horizontal only: unlike a Drifter, a Warden never changes row to
          // route around an obstacle. A cast block in its path is a complete
          // stop, not a detour - it cannot fly up and over the way a Drifter
          // would when the player is elevated. What differs from a Drifter is
          // decided here (allowVertical) plus what can kill it, decided in
          // doCast and fireRanged.
          this.updateDrifter(c, false);
          break;
        case ChimeraKind.Sower: {
          const tx = Math.floor(c.x);
          const ty = Math.floor(c.y);
          // A capped Sower is permanently dormant. That is the whole puzzle.
          if (isSolid(s, tx, ty)) {
            c.timer = SOWER_INTERVAL;
            break;
          }
          c.timer -= DT;
          if (c.timer <= 0) {
            c.timer += SOWER_INTERVAL;
            spawned.push({
              id: s.nextId++,
              kind: ChimeraKind.Creep,
              x: tx + 0.5,
              y: ty + 1,
              vy: 0,
              dir: 1,
              timer: 0,
              alive: true,
            });
          }
          break;
        }
      }
    }

    for (const c of spawned) s.chimera.push(c);
  }

  private updateCreep(c: Chimera): void {
    const s = this.state;

    c.vy += GRAVITY * DT;
    if (c.vy > MAX_FALL_SPEED) c.vy = MAX_FALL_SPEED;
    c.y += c.vy * DT;
    if (c.vy > 0) {
      const ty = Math.floor(c.y - EPS);
      const x0 = Math.floor(c.x - CREEP_HALF_W + EPS);
      const x1 = Math.floor(c.x + CREEP_HALF_W - EPS);
      for (let tx = x0; tx <= x1; tx++) {
        if (isSolid(s, tx, ty)) {
          c.y = ty;
          c.vy = 0;
          break;
        }
      }
    }

    const feetRow = Math.floor(c.y - EPS);
    const probeX = c.x + c.dir * (CREEP_HALF_W + 0.05);
    const aheadTx = Math.floor(probeX);
    const blocked = isSolid(s, aheadTx, feetRow) || isSolid(s, aheadTx, feetRow - 1);
    const ledge = !isSolid(s, aheadTx, feetRow + 1);
    if (blocked || ledge) {
      c.dir = c.dir === 1 ? -1 : 1;
      return;
    }
    c.x += c.dir * CREEP_SPEED * DT;
  }

  /**
   * Steps one tile toward the player on a fixed interval, blocked by any solid
   * tile. Fully deterministic: you beat one of these with architecture, not
   * speed.
   *
   * `allowVertical` is the only difference between a Drifter and a Warden. A
   * Drifter (true) will change row to route around an obstacle when the
   * player is above or below it - a real flying chase, matching the brief's
   * "you defeat it with architecture" only in the sense that a wall on its
   * own row still stops it; height does not. A Warden (false) never
   * considers a vertical move at all: its target is always [dx, 0], so a
   * blocked row is a full stop, not a detour. It cannot climb.
   */
  private updateDrifter(c: Chimera, allowVertical: boolean): void {
    const s = this.state;
    c.timer -= DT;
    if (c.timer > 0) return;
    c.timer += DRIFTER_STEP_INTERVAL;

    const p = s.player;
    const px = Math.floor(p.x);
    const py = Math.floor(p.y - EPS);
    const dx = Math.sign(px - c.x);
    const dy = allowVertical ? Math.sign(py - c.y) : 0;
    if (dx === 0 && dy === 0) return;

    const xFirst = Math.abs(px - c.x) >= Math.abs(py - c.y);
    const tries: Array<[number, number]> = xFirst
      ? [
          [dx, 0],
          [0, dy],
        ]
      : [
          [0, dy],
          [dx, 0],
        ];

    for (const [mx, my] of tries) {
      if (mx === 0 && my === 0) continue;
      const nx = c.x + mx;
      const ny = c.y + my;
      if (!inBounds(nx, ny)) continue;
      if (isSolid(s, nx, ny)) continue;
      c.x = nx;
      c.y = ny;
      return;
    }
  }

  // ------------------------------------------------------------ collisions

  private checkContact(): void {
    const s = this.state;
    if (s.outcome !== Outcome.Playing) return;
    const p = s.player;
    const pl = p.x - PLAYER_HALF_W;
    const pr = p.x + PLAYER_HALF_W;
    const ptop = p.y - PLAYER_H;
    const pbot = p.y;

    for (const c of s.chimera) {
      if (!c.alive) continue;
      let l: number;
      let r: number;
      let t: number;
      let b: number;
      if (c.kind === ChimeraKind.Creep) {
        l = c.x - CREEP_HALF_W;
        r = c.x + CREEP_HALF_W;
        t = c.y - CREEP_H;
        b = c.y;
      } else {
        l = c.x + 0.1;
        r = c.x + 0.9;
        t = c.y + 0.1;
        b = c.y + 0.9;
      }
      if (pr > l && pl < r && pbot > t && ptop < b) {
        this.fail(FailReason.Chimera);
        return;
      }
    }
  }

  private checkItemsAndPortal(buttons: InputFrame): void {
    const s = this.state;
    s.portalPrompt = false;
    if (s.outcome !== Outcome.Playing) return;
    const p = s.player;

    for (const it of s.items) {
      if (it.taken || it.hidden) continue;
      if (!this.tileOverlapsPlayer(it.tx, it.ty)) continue;
      it.taken = true;
      this.out.push({ type: 'item:taken', kind: it.kind, id: it.id });
      switch (it.kind) {
        case ItemKind.Moonseed:
          p.hasMoonseed = true;
          this.out.push({ type: 'portal:open' });
          break;
        case ItemKind.Moonshard:
          s.moonlight += MOONSHARD_VALUE;
          break;
        case ItemKind.TailCharm:
          p.rangedCharges += 3;
          break;
        case ItemKind.Sunshard:
          // No aim, no charge cost: every living Warden in the room dies the
          // instant this is picked up. The other half of "a skill or an item".
          for (const c of s.chimera) {
            if (!c.alive || c.kind !== ChimeraKind.Warden) continue;
            c.alive = false;
            this.out.push({ type: 'chimera:banished', id: c.id, cause: 'item' });
          }
          break;
        case ItemKind.Lumen:
        case ItemKind.Sprite:
          break;
      }
    }

    const overlapsOpenPortal = !s.portal.hidden && p.hasMoonseed && this.tileOverlapsPlayer(s.portal.tx, s.portal.ty);
    s.portalPrompt = overlapsOpenPortal;
    const activated = (buttons & ~p.prevButtons & Btn.Activate) !== 0;
    if (overlapsOpenPortal && activated) {
      s.outcome = Outcome.Cleared;
      this.out.push({ type: 'room:cleared' });
    }
  }

  /**
   * A door never ends the room (no `Outcome.Cleared`, no moonseed) - it just
   * announces "the player touched this exit" and leaves what happens next to
   * whoever owns room switching (React, not the sim).
   *
   * Like the win-condition portal, walking into a door does nothing by
   * itself: the player must press the edge-triggered Activate button while
   * overlapping it. A destination is always picked, not scenery the player
   * passes through - accidentally wandering into one mid-jump should never
   * silently relocate them. `doorPrompt` mirrors whichever door (if
   * any) is currently in range, purely so the HUD can show "press E", since
   * a button with no on-screen affordance breaks the same "always know what
   * to do" rule the rest of the game holds itself to (brief pillar 5).
   */
  private checkDoors(buttons: InputFrame): void {
    const s = this.state;
    s.doorPrompt = null;
    if (s.outcome !== Outcome.Playing) return;
    const activated = (buttons & ~s.player.prevButtons & Btn.Activate) !== 0;
    for (const door of s.doors) {
      if (door.hidden) continue;
      if (!this.tileOverlapsPlayer(door.tx, door.ty)) continue;
      s.doorPrompt = { label: door.label, locked: door.locked };
      if (!activated) continue;
      this.out.push(
        door.locked
          ? { type: 'door:locked', targetRoomId: door.targetRoomId }
          : { type: 'door:entered', targetRoomId: door.targetRoomId },
      );
    }
  }

  private fail(reason: FailReason_): void {
    const s = this.state;
    s.outcome = Outcome.Failed;
    s.failReason = reason;
    this.out.push({ type: 'room:failed', reason });
  }
}

type FailReason_ = (typeof FailReason)[keyof typeof FailReason];

/** Convenience for tests and tooling: run a whole input sequence to completion. */
export function runSequence(
  room: RoomDef,
  inputs: readonly InputFrame[],
): { sim: Simulation; ticks: number } {
  const sim = new Simulation(room);
  let ticks = 0;
  for (const f of inputs) {
    if (sim.state.outcome !== Outcome.Playing) break;
    sim.step(f);
    ticks++;
  }
  return { sim, ticks };
}

export { GRID_W, GRID_H };
